import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Calendar, Truck, ShieldCheck, Phone, Mail, Bookmark, BookmarkCheck, GitCompare, Globe2, ArrowUpRight, AlertTriangle, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND_ASSOC_STATUSES, FEATURED_TIERS } from '../data/mockData';
import { useSupplier } from '../data/hooks';
import { VerificationBadge, VerificationBadgeFull } from '../components/VerificationBadge';
import { TrustScore } from '../components/TrustScore';
import { useGiwahs } from '../context/GiwahsContext';
import { LoadingState, ErrorState } from '../components/states/AsyncStates';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Img from '../components/Img';

const statusBadgeClass = (status) => {
  const s = BRAND_ASSOC_STATUSES.find(s => s.key === status);
  if (!s) return 'border-black';
  switch (s.tone) {
    case 'success': return 'bg-black text-white border-black';
    case 'neutral': return 'bg-white text-black border-black';
    case 'warning': return 'bg-amber-50 text-amber-900 border-amber-900';
    case 'muted': return 'bg-neutral-100 text-neutral-600 border-neutral-400';
    default: return 'border-black';
  }
};

const SupplierProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: supplier, loading, error, refetch } = useSupplier(slug);
  const { savedSuppliers, compareList, toggleSaved, toggleCompare, recordView } = useGiwahs();
  const [tab, setTab] = useState('overview');
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });

  // Fire supplier_viewed once when the entity resolves (slug-keyed).
  useEffect(() => {
    if (!supplier) return;
    track(EVENTS.SUPPLIER_VIEWED, {
      slug: supplier.slug,
      country: supplier.country,
      type: supplier.type,
      trust_score: supplier.trustScore,
      is_featured: Boolean(supplier.featured),
    });
    recordView(supplier.id);
  }, [supplier?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="mx-auto max-w-2xl px-5 py-24"><LoadingState label="Loading supplier profile…" /></div>;
  }
  if (error) {
    return <div className="mx-auto max-w-2xl px-5 py-24"><ErrorState description={error?.message} onRetry={refetch} /></div>;
  }
  if (!supplier) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="supplier-not-found">
        <div className="label-overline">404</div>
        <h1 className="mt-4 font-display text-4xl tracking-tighter">Supplier not found</h1>
        <Link to="/factories" className="mt-6 inline-block btn-giwahs btn-giwahs-primary">Back to directory</Link>
      </div>
    );
  }

  const isSaved = savedSuppliers.includes(supplier.id);
  const isCompared = compareList.includes(supplier.id);

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Factories', url: '/factories' },
    { name: supplier.name, url: `/supplier/${supplier.slug}` },
  ];

  const submitContact = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in name, email, and message.');
      return;
    }
    toast.success('Message queued — supplier contact requests are MOCK in this MVP.');
    setForm({ name: '', email: '', company: '', message: '' });
  };

  return (
    <div data-testid="supplier-profile-page" className="surface-obsidian text-platinum">
      <SEO
        title={`${supplier.name} — ${supplier.type} in ${supplier.country}`}
        description={`${supplier.tagline}. ${supplier.type} based in ${supplier.city}, ${supplier.country}. MOQ ${supplier.moqMin}+, lead time ${supplier.leadTimeDays} days, Trust Score ${supplier.trustScore}/100.`}
        image={supplier.heroImage}
        type="profile"
        breadcrumbs={breadcrumbItems}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: supplier.name,
          description: supplier.tagline,
          url: `https://giwahs.com/supplier/${supplier.slug}`,
          logo: supplier.heroImage,
          image: supplier.heroImage,
          foundingDate: String(supplier.founded),
          address: { '@type': 'PostalAddress', addressLocality: supplier.city, addressCountry: supplier.country },
          aggregateRating: { '@type': 'AggregateRating', ratingValue: (supplier.trustScore / 20).toFixed(1), bestRating: '5', ratingCount: '50' },
          knowsAbout: supplier.specialties,
        }}
      />
      {/* Breadcrumbs */}
      <div className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-3 md:px-8">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      {/* Hero */}
      <section className="relative border-b border-black bg-[#0A0A0A] text-white">
        <div className="absolute inset-0">
          <Img src={supplier.heroImage} alt="" variant="hero" priority className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-20">
          <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-2 label-overline text-neutral-300 hover:text-white">
            <ArrowLeft size={14} strokeWidth={1.5} /> Back
          </button>

          <div className="mt-8 grid gap-8 md:grid-cols-12">
            <div className="md:col-span-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="label-overline inline-block rounded-full border border-white px-3 py-1 text-white">{supplier.type}</span>
                <span className="font-mono-data text-xs text-neutral-300">{supplier.city}, {supplier.country.toUpperCase()}</span>
              </div>
              <h1 className="mt-5 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', lineHeight: 0.95 }}>
                {supplier.name}
              </h1>
              <p className="mt-5 max-w-2xl text-base text-neutral-300 md:text-lg">{supplier.tagline}</p>
              <div className="mt-7 flex flex-wrap gap-2">
                {(supplier.badges || []).map(b => <VerificationBadge key={b} type={b} size="md" />)}
              </div>
            </div>
            <div className="md:col-span-4">
              <TrustScore score={supplier.trustScore} variant="large" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button
                  onClick={() => toggleSaved(supplier.id)}
                  data-testid="profile-save-btn"
                  className={`btn-giwahs flex items-center justify-center gap-2 ${isSaved ? 'btn-giwahs-dark' : 'btn-giwahs-secondary !text-white !border-white hover:!bg-white hover:!text-black'}`}
                >
                  {isSaved ? <BookmarkCheck size={14} strokeWidth={1.6} /> : <Bookmark size={14} strokeWidth={1.6} />}
                  {isSaved ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={() => toggleCompare(supplier.id)}
                  data-testid="profile-compare-btn"
                  className={`btn-giwahs flex items-center justify-center gap-2 ${isCompared ? 'btn-giwahs-dark' : 'btn-giwahs-secondary !text-white !border-white hover:!bg-white hover:!text-black'}`}
                >
                  <GitCompare size={14} strokeWidth={1.6} />
                  {isCompared ? 'Comparing' : 'Compare'}
                </button>
              </div>
              <Link to={`/rfq?supplier=${supplier.slug}`} data-testid="profile-rfq-btn" className="btn-giwahs btn-giwahs-dark mt-3 flex w-full items-center justify-center gap-2">
                Request Quote <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="grid grid-cols-2 gap-px bg-neutral-200 md:grid-cols-5">
            {[
              ['Founded', supplier.founded, Calendar],
              ['Employees', supplier.employees, Users],
              ['Monthly Capacity', supplier.productionCapacityPerMonth, Globe2],
              ['Lead Time', `${supplier.leadTimeDays} days`, Truck],
              ['Response Rate', `${supplier.responseRate}%`, ShieldCheck],
            ].map(([l, v, I]) => (
              <div key={l} className="bg-white p-5">
                <I size={14} strokeWidth={1.5} className="text-neutral-400" />
                <div className="mt-3 label-overline text-neutral-500">{l}</div>
                <div className="mt-1 font-mono-data text-sm">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="flex flex-wrap gap-2 py-4">
            {['overview', 'gallery', 'brand-associations', 'certifications', 'contact'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t}`}
                className={`label-overline giwahs-tab-ink px-4 py-2 ${tab === t ? 'active' : ''}`}
              >
                {t.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab content */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-20">
          {tab === 'overview' && (
            <div className="grid gap-10 md:grid-cols-12">
              <div className="md:col-span-7">
                {supplier.featured && (
                  <div data-testid="featured-promotional-note" className="mb-6 border border-black bg-[#F5F5F0] p-4">
                    <div className="flex items-start gap-3">
                      <Megaphone size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                      <div className="text-xs leading-relaxed text-neutral-800">
                        <strong className="font-semibold uppercase tracking-wider">Sponsored Placement{FEATURED_TIERS[supplier.featuredTier] ? ` · ${FEATURED_TIERS[supplier.featuredTier].label} Tier` : ''}.</strong>{' '}
                        This supplier appears in Featured placements across Giwahs as a paid promotional listing. <strong className="text-black">Verification badges and trust score are assessed independently by the Giwahs intelligence team and are not influenced by sponsorship.</strong>
                      </div>
                    </div>
                  </div>
                )}
                <span className="label-overline">Factory Overview</span>
                <h2 className="mt-4 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
                  Specialised in {(supplier.specialties?.[0] || 'apparel manufacturing').toLowerCase()} since {supplier.founded || 'establishment'}.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-neutral-700">
                  {supplier.name} operates a {supplier.productionCapacityPerMonth || 'verified'} capacity facility in {supplier.city || 'undisclosed location'}, {supplier.country || ''}. With {supplier.employees || 'operational'} employees, the factory specialises in {(supplier.specialties || []).slice(0, 2).join(' and ') || 'contract manufacturing'} and serves brands across {(supplier.exportMarkets || []).join(', ') || 'global markets'}.
                </p>

                <div className="mt-8">
                  <div className="label-overline">Production Specialties</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(supplier.specialties || []).map(s => (
                      <span key={s} className="giwahs-chip-ink font-mono-data text-xs">{s}</span>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <div className="label-overline">Export Markets</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(supplier.exportMarkets || []).map(m => (
                      <span key={m} className="giwahs-chip-ink font-mono-data text-xs">{m}</span>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <div className="label-overline">Shipping Capability</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(supplier.shipping || []).map(m => (
                      <span key={m} className="giwahs-chip-ink font-mono-data text-xs">{m}</span>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="md:col-span-5">
                <div className="border border-black p-6">
                  <span className="label-overline">Verification Layer</span>
                  <div className="mt-4 grid grid-cols-1 gap-2">
                    {(supplier.badges || []).map(b => <VerificationBadgeFull key={b} type={b} />)}
                  </div>
                </div>

                <div className="mt-5 border border-black p-6">
                  <span className="label-overline">Quick Metrics</span>
                  <div className="mt-4 grid grid-cols-2 gap-4 font-mono-data text-sm">
                    <div>
                      <div className="label-overline text-neutral-500">MOQ Range</div>
                      <div className="mt-1">{supplier.moqMin}–{supplier.moqMax}</div>
                    </div>
                    <div>
                      <div className="label-overline text-neutral-500">Pricing</div>
                      <div className="mt-1">{supplier.pricingIndicator}</div>
                    </div>
                    <div>
                      <div className="label-overline text-neutral-500">Response</div>
                      <div className="mt-1">{supplier.responseTimeHours}h avg</div>
                    </div>
                    <div>
                      <div className="label-overline text-neutral-500">Type</div>
                      <div className="mt-1">{supplier.type}</div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          )}

          {tab === 'gallery' && (
            <div>
              <span className="label-overline">Factory Gallery</span>
              <h2 className="mt-4 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>Inside the facility.</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {supplier.gallery.map((img, i) => (
                  <div key={i} className="aspect-[4/5] overflow-hidden border border-black bg-neutral-100">
                    <Img src={img} alt={`${supplier.name} — production facility photo ${i + 1}`} variant="thumb" className="h-full w-full object-cover" />
                  </div>
                ))}
                <div className="flex aspect-[4/5] items-center justify-center border border-dashed border-black bg-white text-center p-6">
                  <div>
                    <div className="label-overline">Production Video</div>
                    <p className="mt-3 text-sm text-neutral-600">Giwahs Video Verified footage will appear here once submitted and reviewed.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'brand-associations' && (
            <div>
              <div className="flex items-start justify-between gap-6 md:items-center">
                <div>
                  <span className="label-overline">Known Brand Associations</span>
                  <h2 className="mt-4 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>Intelligence brief.</h2>
                </div>
                <Link to="/intelligence" className="hidden md:inline-flex items-center gap-1 label-overline border-b border-black pb-0.5">
                  How this works →
                </Link>
              </div>

              <div className="mt-6 border border-amber-900 bg-amber-50 p-5">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} strokeWidth={1.5} className="mt-0.5 shrink-0 text-amber-900" />
                  <div className="text-sm leading-relaxed text-amber-900">
                    <strong className="font-semibold">Legal disclaimer.</strong> Brand associations below use ILLUSTRATIVE DATA and are categorised by verification status. Giwahs does not claim that this factory currently produces for any high-end or well-known brand unless explicitly marked <em>Verified</em> through legally sourced documentation. Always conduct independent due diligence.
                  </div>
                </div>
              </div>

              <div className="mt-8 grid gap-3 md:grid-cols-4">
                {BRAND_ASSOC_STATUSES.map(s => (
                  <div key={s.key} className="border border-neutral-300 p-4">
                    <span className={`label-overline rounded-full border px-3 py-1 ${statusBadgeClass(s.key)}`}>{s.key}</span>
                    <p className="mt-3 text-xs leading-relaxed text-neutral-600">{s.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 border border-black">
                <div className="grid grid-cols-12 gap-3 border-b border-black bg-[#F5F5F0] px-5 py-3 label-overline">
                  <div className="col-span-4">Brand</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-2">Period</div>
                  <div className="col-span-3">Source</div>
                </div>
                {supplier.knownBrandAssociations.map((a, i) => (
                  <div key={i} data-testid={`brand-assoc-${i}`} className="grid grid-cols-12 items-center gap-3 border-b border-neutral-200 px-5 py-4 last:border-b-0">
                    <div className="col-span-4">
                      <div className="font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{a.brand}</div>
                      <div className="font-mono-data text-[10px] text-neutral-400">ILLUSTRATIVE</div>
                    </div>
                    <div className="col-span-3">
                      <span className={`label-overline rounded-full border px-3 py-1 ${statusBadgeClass(a.status)}`}>{a.status}</span>
                    </div>
                    <div className="col-span-2 font-mono-data text-xs text-neutral-700">{a.period}</div>
                    <div className="col-span-3 text-xs leading-relaxed text-neutral-600">{a.source}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'certifications' && (
            <div>
              <span className="label-overline">Certifications</span>
              <h2 className="mt-4 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>Compliance & standards.</h2>
              <div className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                {(supplier.certifications || []).map(c => (
                  <div key={c} data-testid={`cert-${c.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-start gap-3 border border-black p-4">
                    <ShieldCheck size={18} strokeWidth={1.5} />
                    <div>
                      <div className="font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{c}</div>
                      <div className="font-mono-data text-[10px] text-neutral-400">VERIFICATION PENDING</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'contact' && (
            <div className="grid gap-10 md:grid-cols-12">
              <div className="md:col-span-5">
                <span className="label-overline">Contact Supplier</span>
                <h2 className="mt-4 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
                  Send a message.
                </h2>
                <p className="mt-4 text-neutral-700">Avg response: <span className="font-mono-data">{supplier.responseTimeHours}h</span> — response rate {supplier.responseRate}%.</p>
                <div className="mt-8 space-y-3 text-sm">
                  <div className="flex items-center gap-3"><MapPin size={14} strokeWidth={1.5} /> {supplier.city}, {supplier.country}</div>
                  <div className="flex items-center gap-3"><Mail size={14} strokeWidth={1.5} /> contact (proxied via Giwahs)</div>
                  <div className="flex items-center gap-3"><Phone size={14} strokeWidth={1.5} /> Phone routing on request</div>
                </div>
              </div>
              <form onSubmit={submitContact} data-testid="supplier-contact-form" className="border border-black p-6 md:col-span-7">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="border border-black bg-white px-3 py-3 text-sm focus:outline-none" />
                  <input data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email" className="border border-black bg-white px-3 py-3 text-sm focus:outline-none" />
                </div>
                <input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company / Brand" className="mt-4 w-full border border-black bg-white px-3 py-3 text-sm focus:outline-none" />
                <textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Describe your product, quantity, timeline…" rows={6} className="mt-4 w-full border border-black bg-white px-3 py-3 text-sm focus:outline-none" />
                <button type="submit" data-testid="contact-submit" className="btn-giwahs btn-giwahs-primary mt-5 w-full">Send Message</button>
                <p className="mt-3 font-mono-data text-[10px] text-neutral-400">FORM IS UI-ONLY IN MVP — NO MESSAGES ARE SENT.</p>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SupplierProfile;
